import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pushProfilePhotosPath } from '../shellHistory';
import { fetchMyVisits, type ProfileVisit } from '../socialApi';
import { HopperIcon } from '../ui/icons/HopperIcon';
import {
  deleteMedia,
  fetchAllOfficialMedia,
  fetchMediaPage,
  MediaGalleryApiError,
  reorderOfficialMedia,
  setAvatar,
  uploadMediaFile,
  type MediaGalleryMode,
  type MediaItem,
} from './mediaGalleryApi';
import './media-gallery.css';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const PROFILE_PREVIEW_SIZE = 4;

type PendingPhoto = {
  id: string;
  file: File;
  previewUrl: string;
};

type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

type UploadState = {
  id: string;
  name: string;
  progress: number;
  status: UploadStatus;
  error: string | null;
};

export type MediaGalleryProps = {
  mode: MediaGalleryMode;
  entityId: string;
  maxItems?: number;
  canEdit?: boolean;
  onAvatarChange?: (media: { id: string; avatarUrl: string | null }) => void;
  onPendingFilesChange?: (files: File[]) => void;
  onOpenVenue?: (venueId: string) => void;
};

function userMessage(error: unknown, fallback: string): string {
  if (error instanceof MediaGalleryApiError) {
    if (error.status === 413) return 'Фото должно быть не больше 10 МБ';
    if (error.status === 422) return error.message || 'Проверьте выбранный файл';
    if (error.status === 401) return 'Сессия истекла. Войдите снова.';
    return error.message || fallback;
  }
  return fallback;
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.has(file.type)) {
    return 'Поддерживаются только JPEG, PNG и WebP';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'Фото должно быть не больше 10 МБ';
  }
  return null;
}

function isProfilePhotosPath(): boolean {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  return pathname === '/profile/photos';
}

function formatVisitDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function ProfileEmptyState({ editable }: { editable: boolean }) {
  return (
    <div className="media-gallery__empty media-gallery__profile-empty">
      <HopperIcon name="image" size={28} />
      <strong>{editable ? 'Добавьте фото из посещения' : 'Фото из посещений пока нет'}</strong>
      <span>
        {editable
          ? 'Фото в Хоппере привязываются к посещению, подтверждённому чеком.'
          : 'Здесь появятся фотографии из подтверждённых посещений.'}
      </span>
    </div>
  );
}

function EmptyState({ editable }: { editable: boolean }) {
  return (
    <div className="media-gallery__empty">
      <HopperIcon name="image" size={28} />
      <strong>{editable ? 'Добавьте первое фото' : 'Фото пока нет'}</strong>
      <span>{editable ? 'JPEG, PNG или WebP до 10 МБ' : 'Здесь появятся фотографии.'}</span>
    </div>
  );
}

export function MediaGallery({
  mode,
  entityId,
  maxItems = mode === 'impression_attach' ? 5 : Number.POSITIVE_INFINITY,
  canEdit = false,
  onAvatarChange,
  onPendingFilesChange,
  onOpenVenue,
}: MediaGalleryProps) {
  const rootRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingRef = useRef<PendingPhoto[]>([]);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(mode !== 'impression_attach');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [uploadingBatch, setUploadingBatch] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [avatarBusyId, setAvatarBusyId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxAdvancePending, setLightboxAdvancePending] = useState(false);
  const [lightboxImageLoading, setLightboxImageLoading] = useState(false);
  const loadMorePromiseRef = useRef<Promise<number> | null>(null);
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [profileFullView, setProfileFullView] = useState(
    mode === 'user_profile' && canEdit && isProfilePhotosPath(),
  );
  const [profilePreviewOffset, setProfilePreviewOffset] = useState(0);
  const [profileVenueFilter, setProfileVenueFilter] = useState('all');
  const [visits, setVisits] = useState<ProfileVisit[]>([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsLoaded, setVisitsLoaded] = useState(false);
  const [visitPickerOpen, setVisitPickerOpen] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);

  const serverMode = mode !== 'impression_attach';
  const editableServerMode = canEdit && (mode === 'user_profile' || mode === 'venue_official');

  const load = useCallback(async () => {
    if (!serverMode) return;

    setLoading(true);
    setError(null);
    try {
      if (mode === 'venue_official') {
        const all = await fetchAllOfficialMedia(entityId);
        setItems(all);
        setCursor(null);
        setHasMore(false);
      } else {
        const reference = mode === 'user_profile' && canEdit ? 'me' : entityId;
        const page = await fetchMediaPage(mode, reference, null);
        setItems(page.data);
        setCursor(page.meta.next_cursor);
        setHasMore(page.meta.has_more);
      }
    } catch (loadError) {
      setError(userMessage(loadError, 'Не удалось загрузить фото'));
    } finally {
      setLoading(false);
    }
  }, [canEdit, entityId, mode, serverMode]);

  const loadVisits = useCallback(async () => {
    if (mode !== 'user_profile' || !canEdit || visitsLoading) return;
    setVisitsLoading(true);
    try {
      const response = await fetchMyVisits('all', 50);
      setVisits(response.visits.items);
      setVisitsLoaded(true);
    } catch {
      setError('Не удалось загрузить подтверждённые посещения');
    } finally {
      setVisitsLoading(false);
    }
  }, [canEdit, mode, visitsLoading]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    pendingRef.current = pending;
    onPendingFilesChange?.(pending.map((item) => item.file));
  }, [onPendingFilesChange, pending]);

  useEffect(() => () => {
    pendingRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  }, []);

  useEffect(() => {
    if (mode !== 'user_profile' || !canEdit) return;

    const syncFromPath = () => setProfileFullView(isProfilePhotosPath());
    const syncFromEvent = (event: Event) => {
      const full = (event as CustomEvent<{ full?: boolean }>).detail?.full;
      if (typeof full === 'boolean') setProfileFullView(full);
      else syncFromPath();
    };

    window.addEventListener('popstate', syncFromPath);
    window.addEventListener('hopper:profile-photo-view', syncFromEvent);
    syncFromPath();

    return () => {
      window.removeEventListener('popstate', syncFromPath);
      window.removeEventListener('hopper:profile-photo-view', syncFromEvent);
    };
  }, [canEdit, mode]);

  useEffect(() => {
    if (mode !== 'user_profile' || !canEdit) return;
    const center = rootRef.current?.closest('.self-profile-center');
    const card = rootRef.current?.closest('.profile-media-section');
    center?.classList.toggle('is-profile-photo-gallery-view', profileFullView);
    card?.classList.toggle('is-profile-photo-gallery-card', profileFullView);

    return () => {
      center?.classList.remove('is-profile-photo-gallery-view');
      card?.classList.remove('is-profile-photo-gallery-card');
    };
  }, [canEdit, mode, profileFullView]);

  useEffect(() => {
    if (profileFullView && canEdit && !visitsLoaded && !visitsLoading) {
      void loadVisits();
    }
  }, [canEdit, loadVisits, profileFullView, visitsLoaded, visitsLoading]);

  const visitById = useMemo(() => new Map(visits.map((visit) => [visit.id, visit])), [visits]);

  const profileVenueOptions = useMemo(() => {
    const venues = new Map<string, string>();
    for (const item of items) {
      if (item.venue?.id) venues.set(item.venue.id, item.venue.name?.trim() || 'Место Хоппера');
    }
    return Array.from(venues.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ru'));
  }, [items]);

  const profileDisplayItems = useMemo(() => {
    if (mode !== 'user_profile' || !profileFullView || profileVenueFilter === 'all') return items;
    if (profileVenueFilter === 'legacy') return items.filter((item) => !item.visit_id);
    return items.filter((item) => item.venue?.id === profileVenueFilter);
  }, [items, mode, profileFullView, profileVenueFilter]);

  const lightboxItems = useMemo(
    () => profileDisplayItems.filter((item) => item.moderation_status !== 'rejected'),
    [profileDisplayItems],
  );

  const previewItems = useMemo(
    () => items.filter((item) => item.moderation_status !== 'rejected'),
    [items],
  );

  useEffect(() => {
    const maxOffset = Math.max(0, Math.floor(Math.max(0, previewItems.length - 1) / PROFILE_PREVIEW_SIZE) * PROFILE_PREVIEW_SIZE);
    setProfilePreviewOffset((current) => Math.min(current, maxOffset));
  }, [previewItems.length]);

  const addPendingFiles = (files: File[]) => {
    const available = Math.max(0, maxItems - pending.length);
    if (available <= 0) {
      setError(`Можно прикрепить не больше ${maxItems} фото`);
      return;
    }

    const next: PendingPhoto[] = [];
    for (const file of files.slice(0, available)) {
      const validation = validateFile(file);
      if (validation) {
        setError(validation);
        continue;
      }
      next.push({
        id: `${file.name}:${file.size}:${file.lastModified}:${crypto.randomUUID()}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (files.length > available) setError(`Можно прикрепить не больше ${maxItems} фото`);
    if (next.length > 0) setPending((current) => [...current, ...next]);
  };

  const uploadFiles = async (files: File[]) => {
    if (mode === 'impression_attach') {
      addPendingFiles(files);
      return;
    }
    if (!editableServerMode || uploadingBatch) return;

    const visitIdForBatch = mode === 'user_profile' ? selectedVisitId : null;
    if (mode === 'user_profile' && !visitIdForBatch) {
      setError('Сначала выберите подтверждённое посещение');
      return;
    }

    setError(null);

    const valid: Array<{ file: File; id: string }> = [];
    const validationMessages = new Set<string>();
    for (const file of files) {
      const validation = validateFile(file);
      if (validation) {
        validationMessages.add(validation);
        continue;
      }
      valid.push({ file, id: crypto.randomUUID() });
    }

    if (validationMessages.size > 0) {
      setError(Array.from(validationMessages).join('. '));
    }
    if (valid.length === 0) return;

    const uploadBatchId = valid.length > 1 ? crypto.randomUUID() : undefined;
    const queueIds = new Set(valid.map((entry) => entry.id));

    setUploads((current) => [
      ...current.filter((item) => item.status === 'error'),
      ...valid.map(({ file, id }): UploadState => ({
        id,
        name: file.name,
        progress: 0,
        status: 'queued',
        error: null,
      })),
    ]);
    setUploadingBatch(true);

    try {
      for (const { file, id: uploadId } of valid) {
        setUploads((current) => current.map((item) => (
          item.id === uploadId ? { ...item, status: 'uploading', progress: 0 } : item
        )));

        try {
          const created = await uploadMediaFile(mode, file, {
            visitId: visitIdForBatch ?? undefined,
            uploadBatchId,
            onProgress: (progress) => setUploads((current) => current.map((item) => (
              item.id === uploadId ? { ...item, progress } : item
            ))),
          });

          if (mode !== 'venue_official') {
            setItems((current) => [created, ...current]);
          }
          setUploads((current) => current.map((item) => (
            item.id === uploadId ? { ...item, progress: 100, status: 'done', error: null } : item
          )));
        } catch (uploadError) {
          const message = userMessage(uploadError, 'Не удалось загрузить фото');
          setUploads((current) => current.map((item) => (
            item.id === uploadId ? { ...item, status: 'error', error: message } : item
          )));
        }
      }

      if (mode === 'venue_official') {
        await load();
      }

      await new Promise((resolve) => window.setTimeout(resolve, 1200));
      setUploads((current) => current.filter((item) => !queueIds.has(item.id) || item.status === 'error'));
    } finally {
      setUploadingBatch(false);
      if (mode === 'user_profile') setSelectedVisitId(null);
    }
  };

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    void uploadFiles(Array.from(fileList));
    if (inputRef.current) inputRef.current.value = '';
  };

  const loadMore = (): Promise<number> => {
    if (!serverMode || !cursor || mode === 'venue_official') return Promise.resolve(0);
    if (loadMorePromiseRef.current) return loadMorePromiseRef.current;

    setLoadingMore(true);
    setError(null);

    const request = (async () => {
      try {
        const reference = mode === 'user_profile' && canEdit ? 'me' : entityId;
        const page = await fetchMediaPage(mode, reference, cursor);
        setItems((current) => [...current, ...page.data]);
        setCursor(page.meta.next_cursor);
        setHasMore(page.meta.has_more);
        return page.data.length;
      } catch (loadError) {
        setError(userMessage(loadError, 'Не удалось загрузить следующую страницу'));
        return 0;
      } finally {
        setLoadingMore(false);
        loadMorePromiseRef.current = null;
      }
    })();

    loadMorePromiseRef.current = request;
    return request;
  };

  const goNextLightbox = async () => {
    if (lightboxIndex === null || lightboxItems.length === 0) return;

    if (lightboxIndex < lightboxItems.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
      return;
    }

    if (hasMore) {
      setLightboxAdvancePending(true);
      try {
        const loaded = await loadMore();
        if (loaded > 0) {
          setLightboxIndex((current) => current === null ? null : current + 1);
        }
      } finally {
        setLightboxAdvancePending(false);
      }
      return;
    }

    setLightboxIndex(0);
  };

  useEffect(() => {
    if (
      lightboxIndex === null
      || !hasMore
      || loadingMore
      || lightboxIndex < Math.max(0, lightboxItems.length - 3)
    ) {
      return;
    }

    void loadMore();
  }, [hasMore, lightboxIndex, lightboxItems.length, loadingMore]);

  const activeLightboxItemId = lightboxIndex === null ? null : lightboxItems[lightboxIndex]?.id ?? null;

  useEffect(() => {
    if (!activeLightboxItemId) {
      setLightboxImageLoading(false);
      return;
    }
    setLightboxImageLoading(true);
  }, [activeLightboxItemId]);

  useEffect(() => {
    if (lightboxIndex === null || lightboxItems.length === 0) return;

    for (const item of lightboxItems.slice(lightboxIndex + 1, lightboxIndex + 3)) {
      const image = new Image();
      image.decoding = 'async';
      image.src = item.url;
    }
  }, [lightboxIndex, lightboxItems]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightboxIndex(null);
      if (event.key === 'ArrowLeft' && lightboxItems.length > 0) {
        setLightboxIndex((current) => current === null ? null : (current - 1 + lightboxItems.length) % lightboxItems.length);
      }
      if (event.key === 'ArrowRight' && lightboxItems.length > 0) {
        event.preventDefault();
        void goNextLightbox();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasMore, lightboxIndex, lightboxItems.length, loadingMore]);

  const confirmDelete = async () => {
    if (!deleteTarget || (mode !== 'user_profile' && mode !== 'venue_official')) return;
    setDeleting(true);
    setError(null);
    try {
      const deletedId = deleteTarget.id;
      await deleteMedia(mode, deletedId);
      setItems((current) => current.filter((item) => item.id !== deletedId));
      const currentLightboxItem = lightboxIndex === null ? null : lightboxItems[lightboxIndex] ?? null;
      if (currentLightboxItem?.id === deletedId) setLightboxIndex(null);
      setDeleteTarget(null);
    } catch (deleteError) {
      setError(userMessage(deleteError, 'Не удалось удалить фото'));
    } finally {
      setDeleting(false);
    }
  };

  const makeAvatar = async (item: MediaItem) => {
    if (mode !== 'user_profile' || !canEdit || item.moderation_status === 'rejected') return;
    setAvatarBusyId(item.id);
    setError(null);
    try {
      const result = await setAvatar(item.id);
      setItems((current) => current.map((photo) => ({ ...photo, is_avatar: photo.id === item.id })));
      onAvatarChange?.({ id: item.id, avatarUrl: result.avatar_url });
    } catch (avatarError) {
      setError(userMessage(avatarError, 'Не удалось обновить аватар'));
    } finally {
      setAvatarBusyId(null);
    }
  };

  const moveOfficial = async (sourceId: string, targetId: string) => {
    if (mode !== 'venue_official' || sourceId === targetId) return;
    const previous = items;
    const from = items.findIndex((item) => item.id === sourceId);
    const to = items.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setError(null);

    try {
      await reorderOfficialMedia(next.map((item) => item.id));
      setItems((current) => current.map((item, index) => ({ ...item, sort_order: index })));
    } catch (reorderError) {
      setItems(previous);
      setError(userMessage(reorderError, 'Не удалось сохранить порядок фото'));
    }
  };

  const removePending = (id: string) => {
    setPending((current) => {
      const target = current.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  };

  const onDropZone = (event: React.DragEvent<HTMLDivElement>) => {
    if (mode !== 'venue_official' || !editableServerMode || uploadingBatch) return;
    if (!event.dataTransfer.files.length) return;
    event.preventDefault();
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  const openVisitPicker = () => {
    setSelectedVisitId(null);
    setVisitPickerOpen(true);
    if (!visitsLoaded && !visitsLoading) void loadVisits();
  };

  if (mode === 'impression_attach') {
    return (
      <section className="media-gallery media-gallery--attach" aria-label="Фото к впечатлению">
        <div className="media-gallery__heading">
          <div><strong>Фото</strong><span>До {maxItems} фото, JPEG, PNG или WebP до 10 МБ</span></div>
          <button type="button" className="media-gallery__add" onClick={() => inputRef.current?.click()} disabled={pending.length >= maxItems}>
            <HopperIcon name="upload" size={18} /> Добавить фото
          </button>
        </div>
        <input ref={inputRef} className="media-gallery__file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => handleFiles(event.target.files)} />
        {error && <div className="media-gallery__message is-error" role="status">{error}</div>}
        {pending.length > 0 && (
          <div className="media-gallery__grid media-gallery__grid--pending">
            {pending.map((photo) => (
              <div className="media-gallery__tile" key={photo.id}>
                <img src={photo.previewUrl} alt="Фото к впечатлению" />
                <button className="media-gallery__icon-action" type="button" aria-label="Убрать фото" onClick={() => removePending(photo.id)}>
                  <HopperIcon name="close" size={17} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  const finishedUploads = uploads.filter((upload) => upload.status === 'done' || upload.status === 'error').length;

  if (mode === 'user_profile') {
    const previewPage = previewItems.slice(profilePreviewOffset, profilePreviewOffset + PROFILE_PREVIEW_SIZE);
    const canPreviewPrevious = profilePreviewOffset > 0;
    const canPreviewNext = profilePreviewOffset + PROFILE_PREVIEW_SIZE < previewItems.length;
    const selectedVisit = selectedVisitId ? visits.find((visit) => visit.id === selectedVisitId) ?? null : null;
    const currentLightboxItem = lightboxIndex === null ? null : lightboxItems[lightboxIndex] ?? null;
    const currentVisit = currentLightboxItem?.visit_id ? visitById.get(currentLightboxItem.visit_id) ?? null : null;
    const currentLightboxAspectClass = currentLightboxItem?.width && currentLightboxItem?.height
      ? currentLightboxItem.width / currentLightboxItem.height < 0.9
        ? ' is-portrait'
        : currentLightboxItem.width / currentLightboxItem.height > 1.25
          ? ' is-landscape'
          : ' is-square'
      : ' is-square';

    return (
      <section
        ref={rootRef}
        className={`media-gallery media-gallery--user_profile${profileFullView ? ' is-profile-full' : ' is-profile-preview'}`}
        aria-label={profileFullView ? 'Мои фото из посещений' : 'Фото из посещений'}
      >
        <div className="media-gallery__heading media-gallery__profile-heading">
          <div>
            <strong>{profileFullView ? 'Мои фото' : 'Фото из посещений'}</strong>
            <span>
              {profileFullView
                ? 'Фотографии привязаны к посещениям, подтверждённым чеком.'
                : 'Визуальная история мест, где был гость.'}
            </span>
          </div>
          {canEdit && (
            profileFullView ? (
              <button type="button" className="media-gallery__add" onClick={openVisitPicker} disabled={uploadingBatch}>
                <HopperIcon name="upload" size={18} /> {uploadingBatch ? 'Загружаем…' : 'Добавить фото'}
              </button>
            ) : (
              <button type="button" className="media-gallery__all-photos" onClick={pushProfilePhotosPath}>
                Все фото <HopperIcon name="chevron-right" size={16} />
              </button>
            )
          )}
        </div>

        {canEdit && profileFullView && (
          <input
            ref={inputRef}
            className="media-gallery__file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => handleFiles(event.target.files)}
          />
        )}

        {profileFullView && (
          <div className="media-gallery__profile-toolbar">
            <label>
              <span>Место</span>
              <select value={profileVenueFilter} onChange={(event) => { setProfileVenueFilter(event.target.value); setLightboxIndex(null); }}>
                <option value="all">Все места</option>
                {profileVenueOptions.map(([venueId, venueName]) => (
                  <option value={venueId} key={venueId}>{venueName}</option>
                ))}
                {items.some((item) => !item.visit_id) && <option value="legacy">Без привязки · старые фото</option>}
              </select>
            </label>
            <span>{profileDisplayItems.length.toLocaleString('ru-RU')} фото</span>
          </div>
        )}

        {uploads.length > 0 && profileFullView && (
          <div className="media-gallery__uploads" aria-live="polite">
            <div className="media-gallery__upload-summary">
              <strong>Очередь загрузки</strong>
              <span>{finishedUploads} из {uploads.length}</span>
            </div>
            {uploads.map((upload) => (
              <div className={`media-gallery__upload is-${upload.status}`} key={upload.id}>
                <span>{upload.name}</span>
                <div><i style={{ width: `${upload.status === 'queued' ? 0 : upload.progress}%` }} /></div>
                <small>
                  {upload.status === 'queued' && 'В очереди'}
                  {upload.status === 'uploading' && `${upload.progress}%`}
                  {upload.status === 'done' && 'Готово'}
                  {upload.status === 'error' && 'Ошибка'}
                </small>
                {upload.error && <strong>{upload.error}</strong>}
              </div>
            ))}
          </div>
        )}

        {error && <div className="media-gallery__message is-error" role="status">{error}</div>}

        {loading ? (
          <div className="media-gallery__loading">Загружаем фото…</div>
        ) : items.length === 0 ? (
          <ProfileEmptyState editable={canEdit && profileFullView} />
        ) : profileFullView ? (
          profileDisplayItems.length === 0 ? (
            <div className="media-gallery__empty"><strong>В этом месте фото пока нет</strong><span>Выберите другое место или вернитесь к списку всех фото.</span></div>
          ) : (
            <div className="media-gallery__grid media-gallery__profile-grid">
              {profileDisplayItems.map((item) => {
                const rejected = item.moderation_status === 'rejected';
                const lightboxPosition = lightboxItems.findIndex((photo) => photo.id === item.id);
                const visit = item.visit_id ? visitById.get(item.visit_id) ?? null : null;
                return (
                  <article className="media-gallery__profile-tile" key={item.id}>
                    {rejected ? (
                      <div className="media-gallery__rejected">
                        <HopperIcon name="error" size={24} />
                        <strong>Фото не прошло проверку</strong>
                        <span>Откройте управление и удалите его.</span>
                      </div>
                    ) : (
                      <button className="media-gallery__image-button" type="button" onClick={() => lightboxPosition >= 0 && setLightboxIndex(lightboxPosition)}>
                        <img src={item.thumbnail_url ?? item.url} alt={item.venue?.name ? `Фото из ${item.venue.name}` : 'Фото из посещения'} loading="lazy" />
                      </button>
                    )}
                    <div className="media-gallery__profile-caption">
                      {item.venue?.id && item.venue.name ? (
                        <button type="button" onClick={() => onOpenVenue?.(item.venue!.id)} disabled={!onOpenVenue}>
                          <HopperIcon name="pin" size={14} /> {item.venue.name}
                        </button>
                      ) : (
                        <strong>Без привязки к посещению</strong>
                      )}
                      <span>{formatVisitDate(visit?.local_date) ?? (item.visit_id ? 'Подтверждённое посещение' : 'Старое фото')}</span>
                    </div>
                    {item.is_avatar && <span className="media-gallery__badge"><HopperIcon name="profile" size={14} /> Аватар</span>}
                  </article>
                );
              })}
            </div>
          )
        ) : previewItems.length === 0 ? (
          <ProfileEmptyState editable={false} />
        ) : (
          <div className="media-gallery__preview-shell">
            <button
              type="button"
              className="media-gallery__preview-arrow is-left"
              aria-label="Предыдущие фото"
              disabled={!canPreviewPrevious}
              onClick={() => setProfilePreviewOffset((current) => Math.max(0, current - PROFILE_PREVIEW_SIZE))}
            >
              <HopperIcon name="chevron-left" size={20} />
            </button>
            <div className="media-gallery__preview-grid">
              {previewPage.map((item) => {
                const position = lightboxItems.findIndex((photo) => photo.id === item.id);
                return (
                  <button className="media-gallery__preview-photo" type="button" key={item.id} onClick={() => position >= 0 && setLightboxIndex(position)}>
                    <img src={item.thumbnail_url ?? item.url} alt={item.venue?.name ? `Фото из ${item.venue.name}` : 'Фото'} loading="lazy" />
                    {item.venue?.name && <span>{item.venue.name}</span>}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="media-gallery__preview-arrow is-right"
              aria-label="Следующие фото"
              disabled={!canPreviewNext}
              onClick={() => setProfilePreviewOffset((current) => current + PROFILE_PREVIEW_SIZE)}
            >
              <HopperIcon name="chevron-right" size={20} />
            </button>
          </div>
        )}

        {profileFullView && hasMore && (
          <button type="button" className="media-gallery__more" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Загружаем…' : 'Показать ещё'}
          </button>
        )}

        {visitPickerOpen && canEdit && profileFullView && (
          <div className="media-gallery__modal" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setVisitPickerOpen(false)}>
            <div className="media-gallery__visit-picker" role="dialog" aria-modal="true" aria-labelledby="media-visit-title">
              <div className="media-gallery__visit-picker-heading">
                <div>
                  <span>Добавить фото</span>
                  <h3 id="media-visit-title">Выберите посещение</h3>
                  <p>Фото будет связано с конкретной датой и заведением.</p>
                </div>
                <button type="button" aria-label="Закрыть" onClick={() => setVisitPickerOpen(false)}><HopperIcon name="close" size={20} /></button>
              </div>

              {visitsLoading ? (
                <div className="media-gallery__visit-picker-state">Загружаем подтверждённые посещения…</div>
              ) : visits.length === 0 ? (
                <div className="media-gallery__visit-picker-state">
                  <strong>Нет подтверждённых посещений</strong>
                  <span>Сначала подтвердите посещение фискальным чеком — после этого к нему можно добавить фото.</span>
                </div>
              ) : (
                <div className="media-gallery__visit-list">
                  {visits.map((visit) => (
                    <button
                      type="button"
                      className={selectedVisitId === visit.id ? 'is-selected' : ''}
                      key={visit.id}
                      onClick={() => setSelectedVisitId(visit.id)}
                    >
                      <span><strong>{visit.venue.name ?? 'Место Хоппера'}</strong><small>{formatVisitDate(visit.local_date)}</small></span>
                      <span aria-hidden="true">{selectedVisitId === visit.id ? '✓' : '›'}</span>
                    </button>
                  ))}
                </div>
              )}

              {selectedVisit && (
                <div className="media-gallery__visit-selected">
                  <HopperIcon name="pin" size={17} />
                  <span><strong>{selectedVisit.venue.name ?? 'Место Хоппера'}</strong><small>{formatVisitDate(selectedVisit.local_date)}</small></span>
                </div>
              )}

              <div className="media-gallery__visit-picker-actions">
                <button type="button" onClick={() => setVisitPickerOpen(false)}>Отмена</button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={!selectedVisitId || visitsLoading}
                  onClick={() => {
                    setVisitPickerOpen(false);
                    inputRef.current?.click();
                  }}
                >
                  Выбрать фото
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="media-gallery__modal" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setDeleteTarget(null)}>
            <div className="media-gallery__confirm" role="dialog" aria-modal="true" aria-labelledby="media-delete-title">
              <HopperIcon name="trash" size={24} />
              <h3 id="media-delete-title">Удалить это фото?</h3>
              <p>Отменить удаление после подтверждения не получится.</p>
              <div>
                <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Отмена</button>
                <button type="button" className="is-danger" onClick={() => void confirmDelete()} disabled={deleting}>{deleting ? 'Удаляем…' : 'Удалить'}</button>
              </div>
            </div>
          </div>
        )}

        {currentLightboxItem && lightboxIndex !== null && (
          <div className="media-gallery__lightbox media-gallery__profile-lightbox" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setLightboxIndex(null)}>
            <button type="button" className="media-gallery__lightbox-close" aria-label="Закрыть" onClick={() => setLightboxIndex(null)}>
              <HopperIcon name="close" size={24} />
            </button>
            {lightboxItems.length > 1 && (
              <button type="button" className="media-gallery__lightbox-nav is-left" aria-label="Предыдущее фото" onClick={() => setLightboxIndex((lightboxIndex - 1 + lightboxItems.length) % lightboxItems.length)}>
                <HopperIcon name="chevron-left" size={28} />
              </button>
            )}
            <div className={`media-gallery__profile-lightbox-content${currentLightboxAspectClass}`}>
              <div className="media-gallery__profile-lightbox-media">
                <img
                  key={currentLightboxItem.id}
                  src={currentLightboxItem.url}
                  width={currentLightboxItem.width ?? undefined}
                  height={currentLightboxItem.height ?? undefined}
                  alt="Фото в полном размере"
                  decoding="async"
                  onLoad={() => setLightboxImageLoading(false)}
                  onError={() => setLightboxImageLoading(false)}
                />
                {(lightboxAdvancePending || lightboxImageLoading) && (
                  <div className="media-gallery__lightbox-loading" role="status" aria-live="polite">
                    <HopperIcon name="spinner" size={18} />
                    <span>{lightboxAdvancePending ? 'Загружаем следующее фото…' : 'Загружаем фото…'}</span>
                  </div>
                )}
              </div>
              <aside>
                <span className="media-gallery__lightbox-eyebrow">Фото из посещения</span>
                {currentLightboxItem.venue?.id && currentLightboxItem.venue.name ? (
                  <button type="button" className="media-gallery__lightbox-venue" onClick={() => onOpenVenue?.(currentLightboxItem.venue!.id)} disabled={!onOpenVenue}>
                    <HopperIcon name="pin" size={16} /> {currentLightboxItem.venue.name}
                  </button>
                ) : (
                  <strong>Без привязки к посещению</strong>
                )}
                <span>{formatVisitDate(currentVisit?.local_date) ?? (currentLightboxItem.visit_id ? 'Подтверждённое посещение' : 'Старое фото')}</span>
                {currentLightboxItem.verified && <em>✓ Посещение подтверждено чеком</em>}
                {canEdit && profileFullView && (
                  <div className="media-gallery__lightbox-actions">
                    {!currentLightboxItem.is_avatar && (
                      <button type="button" onClick={() => void makeAvatar(currentLightboxItem)} disabled={avatarBusyId === currentLightboxItem.id}>
                        <HopperIcon name="profile" size={16} /> {avatarBusyId === currentLightboxItem.id ? 'Сохраняем…' : 'Сделать аватаром'}
                      </button>
                    )}
                    {currentLightboxItem.is_avatar && <span>Текущий аватар</span>}
                    <button type="button" className="is-danger" onClick={() => setDeleteTarget(currentLightboxItem)}>
                      <HopperIcon name="trash" size={16} /> Удалить
                    </button>
                  </div>
                )}
              </aside>
            </div>
            {lightboxItems.length > 1 && (
              <button type="button" className="media-gallery__lightbox-nav is-right" aria-label="Следующее фото" onClick={() => void goNextLightbox()} disabled={(loadingMore || lightboxAdvancePending) && lightboxIndex === lightboxItems.length - 1}>
                <HopperIcon name="chevron-right" size={28} />
              </button>
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <section ref={rootRef} className={`media-gallery media-gallery--${mode}`} aria-label="Галерея фото">
      <div className="media-gallery__heading">
        <div>
          <strong>{mode === 'venue_official' ? 'Фото заведения' : 'Фото'}</strong>
          {mode === 'venue_official' && <span>Перетащите фото, чтобы изменить порядок.</span>}
        </div>
        {editableServerMode && (
          <button type="button" className="media-gallery__add" onClick={() => inputRef.current?.click()} disabled={uploadingBatch}>
            <HopperIcon name="upload" size={18} /> {uploadingBatch ? 'Загружаем…' : 'Добавить фото'}
          </button>
        )}
      </div>

      {editableServerMode && (
        <input ref={inputRef} className="media-gallery__file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => handleFiles(event.target.files)} />
      )}

      {editableServerMode && (
        <div
          className={`media-gallery__dropzone${uploadingBatch ? ' is-disabled' : ''}`}
          onDragOver={(event) => {
            if (!uploadingBatch) event.preventDefault();
          }}
          onDrop={onDropZone}
        >
          <HopperIcon name="image" size={20} />
          <span>{uploadingBatch ? 'Дождитесь завершения текущей очереди' : 'Можно перетащить фото сюда'}</span>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="media-gallery__uploads" aria-live="polite">
          <div className="media-gallery__upload-summary">
            <strong>Очередь загрузки</strong>
            <span>{finishedUploads} из {uploads.length}</span>
          </div>
          {uploads.map((upload) => (
            <div className={`media-gallery__upload is-${upload.status}`} key={upload.id}>
              <span>{upload.name}</span>
              <div><i style={{ width: `${upload.status === 'queued' ? 0 : upload.progress}%` }} /></div>
              <small>
                {upload.status === 'queued' && 'В очереди'}
                {upload.status === 'uploading' && `${upload.progress}%`}
                {upload.status === 'done' && 'Готово'}
                {upload.status === 'error' && 'Ошибка'}
              </small>
              {upload.error && <strong>{upload.error}</strong>}
            </div>
          ))}
        </div>
      )}

      {error && <div className="media-gallery__message is-error" role="status">{error}</div>}

      {loading ? (
        <div className="media-gallery__loading">Загружаем фото…</div>
      ) : items.length === 0 ? (
        <EmptyState editable={editableServerMode} />
      ) : (
        <div className="media-gallery__grid">
          {items.map((item) => {
            const rejected = item.moderation_status === 'rejected';
            const lightboxPosition = lightboxItems.findIndex((photo) => photo.id === item.id);
            return (
              <article
                key={item.id}
                className={`media-gallery__tile${dragOverId === item.id ? ' is-drag-over' : ''}${draggedId === item.id ? ' is-dragging' : ''}`}
                draggable={mode === 'venue_official' && canEdit}
                onDragStart={() => setDraggedId(item.id)}
                onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
                onDragOver={(event) => {
                  if (mode !== 'venue_official' || !canEdit) return;
                  event.preventDefault();
                  setDragOverId(item.id);
                }}
                onDrop={(event) => {
                  if (mode !== 'venue_official' || !canEdit || !draggedId) return;
                  event.preventDefault();
                  setDragOverId(null);
                  void moveOfficial(draggedId, item.id);
                }}
              >
                {rejected ? (
                  <div className="media-gallery__rejected">
                    <HopperIcon name="error" size={24} />
                    <strong>Фото не прошло проверку</strong>
                    <span>Можно удалить его и загрузить другое.</span>
                  </div>
                ) : (
                  <button className="media-gallery__image-button" type="button" onClick={() => lightboxPosition >= 0 && setLightboxIndex(lightboxPosition)}>
                    <img src={item.thumbnail_url ?? item.url} alt="Фото" loading="lazy" />
                  </button>
                )}

                {editableServerMode && (
                  <div className="media-gallery__actions">
                    <button type="button" className="is-danger" onClick={() => setDeleteTarget(item)}>
                      <HopperIcon name="trash" size={16} /> Удалить
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {hasMore && (
        <button type="button" className="media-gallery__more" onClick={() => void loadMore()} disabled={loadingMore}>
          {loadingMore ? 'Загружаем…' : 'Показать ещё'}
        </button>
      )}

      {deleteTarget && (
        <div className="media-gallery__modal" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setDeleteTarget(null)}>
          <div className="media-gallery__confirm" role="dialog" aria-modal="true" aria-labelledby="media-delete-title">
            <HopperIcon name="trash" size={24} />
            <h3 id="media-delete-title">Удалить это фото?</h3>
            <p>Отменить удаление после подтверждения не получится.</p>
            <div>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Отмена</button>
              <button type="button" className="is-danger" onClick={() => void confirmDelete()} disabled={deleting}>{deleting ? 'Удаляем…' : 'Удалить'}</button>
            </div>
          </div>
        </div>
      )}

      {lightboxIndex !== null && lightboxItems[lightboxIndex] && (
        <div className="media-gallery__lightbox" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setLightboxIndex(null)}>
          <button type="button" className="media-gallery__lightbox-close" aria-label="Закрыть" onClick={() => setLightboxIndex(null)}>
            <HopperIcon name="close" size={24} />
          </button>
          {lightboxItems.length > 1 && (
            <button type="button" className="media-gallery__lightbox-nav is-left" aria-label="Предыдущее фото" onClick={() => setLightboxIndex((lightboxIndex - 1 + lightboxItems.length) % lightboxItems.length)}>
              <HopperIcon name="chevron-left" size={28} />
            </button>
          )}
          <img src={lightboxItems[lightboxIndex].url} alt="Фото в полном размере" />
          {lightboxItems.length > 1 && (
            <button type="button" className="media-gallery__lightbox-nav is-right" aria-label="Следующее фото" onClick={() => void goNextLightbox()} disabled={loadingMore && lightboxIndex === lightboxItems.length - 1}>
              <HopperIcon name="chevron-right" size={28} />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
